import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DateHelper } from 'apps/idl/src/helpers';
import { ArrayHelper } from 'apps/idl/src/helpers/arrayHelper';
import { IdHelper } from 'apps/idl/src/helpers/idHelper';
import { EPrefix, ESortOrder, UserData } from 'apps/idl/src/model';
import { ConfigData } from 'apps/idl/src/model/config/ConfigData';
import { EDatabaseRole } from 'apps/idl/src/model/store/EDatabaseRole';
import { IDataSource } from 'apps/idl/src/model/store/IDataSource';
import { PlatformService } from 'apps/idl/src/services/security';
import { ShowMessageParamsPopup } from 'apps/idl/src/services/security/interfaces/ShowMessageParamsPopup';
import { Store } from 'apps/idl/src/services/store.service';
import { UiMessageService } from 'apps/idl/src/services/security/uiMessage.service';
import { WorkspaceService } from 'apps/idl/src/services/security/workspace.service';
import { StoredSeance } from 'apps/idl/src/anakin/models/StoredSeance';
import { plainToClass } from 'class-transformer';
import { Observable, Subject, forkJoin, from, of, throwError } from 'rxjs';
import { catchError, concatMap, finalize, map, mapTo, mergeMap, switchMap, tap, toArray } from 'rxjs/operators';
import { C_PREFIX_AMC, C_PREFIX_AMCP, C_PREFIX_AMO, C_PREFIX_AMOP } from '../../../app/app.constants';
import { IdlPrestation } from '../../facturation/models/idl-prestation';
import { AMCP } from '../model/amc-p';
import { AMOP } from '../model/amo-p';
import { ECouvertureType } from '../model/ecouverture-type.enum';
import { EGarantie } from '../model/egarantie.enum';
import { EUpdateMode } from '../model/eupdate-mode.enum';
import { IAMC } from '../model/iamc';
import { IAMCP } from '../model/iamc-p';
import { IAMO } from '../model/iamo';
import { IAMOP } from '../model/iamo-p';
import { IConvention } from '../model/iconvention';
import { ICouvertureP } from '../model/icouverture-p';
import { ISortedCouvertures } from '../model/isorted-couvertures';
import { AuthenticatedRequestOptionBuilder } from 'apps/idl/src/model/api/authenticated-request-option-builder';
import { ChoixConventionComponent } from 'apps/idl/src/anakin/features/patients/components/choix-convention/choix-convention.component';
import { DrawerPopoverService } from 'apps/idl/src/anakin/features/shared/services/drawer-popover.service';
import { LoaderService } from 'apps/idl/src/anakin/features/shared/services/loader.service';
import { PanneauService } from 'apps/idl/src/anakin/features/shared/services/panneau.service';
import { ISelectOption } from '../../selector/selector/ISelectOption';

@Injectable({
	providedIn: 'root'
})
export class CouverturesService {

	//#region PROPERTIES

	public static readonly C_COUVERTURES_GESTION_UNIQUE_IDS: string[] = [
		"amc_0099999997", // C2S
		"amc_0775685399", // MGEN
	];

	private refreshCouvertureSubject = new Subject<void>();
	refreshCouverture$ = this.refreshCouvertureSubject.asObservable();

	//#endregion

	//#region METHODS

	constructor(
		private readonly isvcStore: Store,
		private readonly isvcWorkspace: WorkspaceService,
		private readonly isvcUiMessage: UiMessageService,
		private readonly ioHttp: HttpClient,
		private readonly svcLoader: LoaderService,
		private readonly svcDrawerPopover: DrawerPopoverService,
		private readonly svcPanneau: PanneauService,
		private readonly svcPlatform: PlatformService,
	) { }

	triggerRefreshCouvertureSubject() {
		this.refreshCouvertureSubject.next();
	}

	private get baseUrl(): string {
		return `${ConfigData.environment.cloud_url}${ConfigData.environment.cloud_api_apps_suffix}workspaces/${this.isvcWorkspace.getWorkspaceNameFromId(ArrayHelper.getFirstElement(UserData.current.workspaceInfos).id)}`;
	}

	public getPatientSortedCouvertures(psPatientId: string, pbLive?: boolean): Observable<{ AMOPs: AMOP[]; AMCPs: AMCP[]; }> {
		const loAMOPDataSource: IDataSource = this.getCouvertureDataSource(psPatientId, C_PREFIX_AMOP, pbLive);
		const loAMCDataSource: IDataSource = this.getCouvertureDataSource(psPatientId, C_PREFIX_AMCP, pbLive);

		let laAMOPs: AMOP[] = [];
		let laAMCPs: AMCP[] = [];

		return this.isvcStore.get<IAMOP>(loAMOPDataSource)
			.pipe(
				tap((paResults: IAMOP[]) => laAMOPs = paResults.map((poAMOP: IAMOP) => plainToClass(AMOP, poAMOP))),
				mergeMap(() => this.isvcStore.get<IAMCP>(loAMCDataSource)),
				tap((paResults: IAMCP[]) => laAMCPs = paResults.map((poAMCP: IAMCP) => plainToClass(AMCP, poAMCP))),
				map(() => {
					return {
						AMOPs: ArrayHelper.dynamicSort(laAMOPs, "dateFin", ESortOrder.descending),
						AMCPs: ArrayHelper.dynamicSort(laAMCPs, "dateFin", ESortOrder.descending)
					};
				})
			);
	}

	public deleteAllCouvertureAMO(idPatient: string): Observable<void> {
		return this.getPatientSortedCouvertures(idPatient).pipe(
			mergeMap((sortedCouvertures) => {
				const amopsToDelete = sortedCouvertures.AMOPs;
				if (amopsToDelete.length === 0) {
					return of(undefined);
				}

				const deleteObservables: Observable<void>[] = amopsToDelete
					.filter(amop => {
						const isManual = amop.updateMode !== EUpdateMode.ADRi && amop.updateMode !== EUpdateMode.carteVitale;
						const isOutsideSameHour = !DateHelper.areDatesInSameHour(new Date(), new Date(amop.updateDate));

						return isManual || isOutsideSameHour;
					})
					.map(amop =>
						this.deleteCouverture(amop, idPatient).pipe(
							map(() => undefined)
						)
					);

				return forkJoin(deleteObservables).pipe(
					map(() => undefined)
				);
			}),
			catchError((error) => {
				return throwError(error);
			})
		);
	}


	public getPatientLastSortedCouvertures(psPatientId: string, pbLive?: boolean): Observable<ISortedCouvertures> {
		return this.getPatientSortedCouvertures(psPatientId, pbLive)
			.pipe(
				map((poCouvertures: { AMOPs: AMOP[]; AMCPs: AMCP[]; }) => {
					return {
						AMOP: ArrayHelper.getFirstElement(poCouvertures.AMOPs),
						AMCP: ArrayHelper.getFirstElement(poCouvertures.AMCPs)
					};
				})
			);
	}

	public getCouvertureDataSource(psPatientId: string, peCouverturePrefix: EPrefix, pbLive: boolean): IDataSource {
		return {
			databasesIds: this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.workspace),
			viewParams: {
				include_docs: true,
				startkey: `${peCouverturePrefix}${psPatientId}`,
				endkey: `${peCouverturePrefix}${psPatientId}${Store.C_ANYTHING_CODE_ASCII}`
			},
			live: pbLive
		};
	}

	public saveCouvertureANAKIN(poCouverture: ICouvertureP, anchorPopover?: any): Observable<ICouvertureP> {
		this.svcLoader.showLoader(`Sauvegarde de la couverture ${poCouverture._id.startsWith(EPrefix.amo) ? 'AMO' : 'AMC'} en cours ...`)
		return from(this.isvcStore.put(poCouverture)).pipe(
			switchMap(() =>
				this.svcPlatform.isMobileApp
					? this.isvcStore.replicateSingleDocument(poCouverture._id)
					: of(null)
			),			
			switchMap(() => {
				const patientId = IdHelper.extractAllIds(poCouverture._id)[0];
				return this.getPatientLastSortedCouvertures(patientId).pipe(
					switchMap(lastSortedCouvertures => {
						const exportObservablesList = [];
						if (poCouverture._id.startsWith(EPrefix.amc)) {
							exportObservablesList.push(
								this.exportCouvertureANAKIN(patientId, poCouverture as AMCP, anchorPopover).pipe(
									catchError(error => {
										console.error('Erreur lors de l\'exportation de l\'AMCP :', error);
										throw (error);
									})
								)
							);
						} else {
							let onlyAmoUpdate: boolean = !lastSortedCouvertures.AMCP;
							exportObservablesList.push(
								this.exportCouvertureANAKIN(patientId, poCouverture as AMOP, anchorPopover, onlyAmoUpdate).pipe(
									catchError(error => {
										console.error('Erreur lors de l\'exportation de l\'AMOP :', error);
										throw ({ error: { message: 'Erreur lors de l\'exportation de l\'AMO' } })
									})
								)
							);
							if (lastSortedCouvertures.AMCP) {
								//Les conventions de l'amc dépendent de l'AMO (codeAmo) c'est pourquoi on doit faire un exportAMC
								exportObservablesList.push(
									this.exportCouvertureANAKIN(patientId, lastSortedCouvertures.AMCP, anchorPopover).pipe(
										catchError(error => {
											console.error('Erreur lors de l\'exportation de l\'AMCP :', error);
											throw (error);
										})
									)
								);
							}
						}

						return from(exportObservablesList).pipe(
							concatMap(obs => obs),
							toArray()
						);
					})

				);
			}),
			mapTo(poCouverture),
			finalize(() => {
				this.svcLoader.hideLoader();
			})
		);
	}

	/** Supprime une couverture.
	 * @param poCouverture
	 */
	public deleteCouverture(poCouverture: ICouvertureP, patientId?: string): Observable<boolean> {
		const lsTypeCouverture = poCouverture._id.startsWith(EPrefix.amo) ? "amo" : "amc";
		const dbId = ArrayHelper.getFirstElement(this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.workspace));
		this.svcLoader.showLoader("Suppression de la couverture en cours...");
		if (patientId) {
			const params = new HttpParams().set('couvertureId', poCouverture._id);

			return this.ioHttp
				.delete<ICouvertureP>(
					`${this.baseUrl}/entities/patients/${patientId}/${lsTypeCouverture}`,
					{
						params,
						...AuthenticatedRequestOptionBuilder.buildAuthenticatedRequestOptions(),
					}
				)
				.pipe(
					switchMap(() => {
						if (!this.svcPlatform.isMobileApp) return of(null)
						return this.isvcStore.replicateToLocal(dbId, {
							selector: {
								_id: {
									$eq: `${poCouverture._id}`,
								}
							}
						})
					}),
					map(() => true),
					tap(() => {
						this.svcLoader.hideLoader();
					}),
					catchError((error) => {
						this.svcLoader.hideLoader();
						console.error(`Erreur lors de la suppression de la couverture sur le serveur :`, error);
						return of(false);
					})
				);
		}
		else {
			return this.isvcStore.delete(poCouverture).pipe(
				switchMap(() => {
					if (!this.svcPlatform.isMobileApp) return of(null)
					return this.isvcStore.replicateToLocal(dbId, {
						selector: {
							_id: {
								$eq: `${poCouverture._id}`,
							}
						}
					})
				}),
				map((poResponse) => poResponse.ok),
				tap(() => {
					this.svcLoader.hideLoader();
				}),
				catchError((error) => {
					this.svcLoader.hideLoader();
					console.error(`Erreur lors de la suppression locale de la couverture :`, error);
					return of(false);
				})
			);
		}

	}




	public getEtablissements(peCouvertureType: ECouvertureType): Observable<(IAMO | IAMC)[]> {
		const lsDatabaseIds: string[] = this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.formsEntries);
		const lsPrefix: string = peCouvertureType === ECouvertureType.AMOP ? C_PREFIX_AMO : C_PREFIX_AMC;
		const loDataSource: IDataSource = {
			databasesIds: lsDatabaseIds,
			viewParams: {
				include_docs: true,
				startkey: lsPrefix,
				endkey: `${lsPrefix}${Store.C_ANYTHING_CODE_ASCII}`
			}
		};

		return this.isvcStore.get<IAMO | IAMC>(loDataSource);
	}

	public getEtablissementsByIds(paIds: string[]): Observable<(IAMO | IAMC)[]> {
		const lsDatabaseIds: string[] = this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.formsEntries);
		const modifiedPaIds = paIds.flatMap(id => {
			if (id.startsWith("amo_")) {
				const modifiedId = id.substring(0, id.lastIndexOf('-')) + '-0000';
				return [id, modifiedId];
			} else {
				return id;
			}
		});
		const loDataSource: IDataSource = {
			databasesIds: lsDatabaseIds,
			viewParams: {
				include_docs: true,
				keys: modifiedPaIds
			}
		};

		return this.isvcStore.get<IAMO | IAMC>(loDataSource)
	}

	public getLabelCouverture(amoId: string, etablissementsById: Map<string, IAMO | IAMC>): string | undefined {
		const fullLabel = etablissementsById.get(amoId)?.label;
		if (fullLabel) {
			return fullLabel;
		} else {
			const modifiedAmoId = amoId.replace(/-\d+$/, '-0000');
			const resultat = etablissementsById.get(modifiedAmoId);
			return resultat?.label;
		}
	}

	public getEtablissementById(amoId: string, etablissementsById: Map<string, IAMO | IAMC>): IAMO | IAMC {
		const etablissement = etablissementsById.get(amoId);
		if (etablissement) {
			return etablissement;
		}
		const modifiedAmoId = amoId.replace(/-\d+$/, '-0000');
		return etablissementsById.get(modifiedAmoId);
	}

	public exportCouvertureANAKIN(psPatientId: string, poCouverture: AMOP | AMCP, anchorPopover?: any, closeAfterUpdate: boolean = true): Observable<ICouvertureP> {
		const lsTypeCouverture: string = poCouverture._id.startsWith(EPrefix.amo) ? "amo" : "amc";
		const params = new HttpParams().set('couvertureId', poCouverture._id);
		return this.ioHttp.post<ICouvertureP>(
			`${this.baseUrl}/entities/patients/${psPatientId}/${lsTypeCouverture}/export`,
			null,
			{
				params: params,
				...AuthenticatedRequestOptionBuilder.buildAuthenticatedRequestOptions()
			}
		).pipe(
			mergeMap((response) => {
				if (lsTypeCouverture === "amc" && !(poCouverture as AMCP).gestionUnique) {
					const couverture: AMCP = poCouverture as AMCP;
					return this.consulterConventions(psPatientId).pipe(
						map(resultat => !Array.isArray(resultat) ? [resultat] : resultat),
						map(conventions => ArrayHelper.getFirstElement(conventions)),
						switchMap(conventionSelected => {
							couverture.idConvention = conventionSelected.idtConvention;
							couverture.convention = conventionSelected;
							return this.isvcStore.put(couverture);
						}),
						catchError(error => {
							console.error("Error during the second consultation: ", error);
							return of(undefined);
						}),
						finalize(() => {
							this.triggerRefreshCouvertureSubject();
							this.svcDrawerPopover.close();
							this.svcPanneau.close();
						})
					);
				} else {
					if(closeAfterUpdate){
						this.triggerRefreshCouvertureSubject();
						this.svcDrawerPopover.close();
						this.svcPanneau.close();
					}
					return of(response);
				}
			}),
			catchError(poError => {
				if (lsTypeCouverture === "amo") {
					console.error("PAT.S:: Erreur export de la couverture amo :", poError);
					throw (poError);
				} else {
					const couverture: AMCP = poCouverture as AMCP;
					delete couverture.idConvention;
					delete couverture.convention;
					if (poError.error.message.includes("ne possède aucune convention")) {
						// pas de convention
						throw (poError);
					} else if (poError.error.message.includes("possède plusieurs conventions")) {
						return this.consulterConventions(psPatientId).pipe(
							tap(result => {
								const resultatArrayConv: IConvention[] = !Array.isArray(result) ? [result] : result;
								this.svcDrawerPopover.open("Sélectionner la convention", "50%", anchorPopover?.nativeElement, ChoixConventionComponent, {
									listConventions: resultatArrayConv,
									patientId: psPatientId,
									couvertureAMC: couverture
								}, null)
							}),
							catchError(error => {
								throw (error);
							})
						);
					}
					else {
						throw (poError);
					}
				}
			})
		)
	}

	public consulterConventions(psPatientId: string): Observable<IConvention[]> {
		return this.ioHttp.post<IConvention[]>(
			`${this.baseUrl}/entities/patients/${psPatientId}/amc/consulterConventions`,
			null,
			AuthenticatedRequestOptionBuilder.buildAuthenticatedRequestOptions()
		)
			.pipe(
				catchError(poError => {
					console.error("PAT.S:: Erreur l'appel à consulter convention :", poError);
					this.isvcUiMessage.showMessage(
						new ShowMessageParamsPopup({ header: `Erreur dans la consultation des conventions.`, message: poError.error ? poError.error.message : poError.message })
					);
					throw (poError);
				}),
			);
	}

	public selectionnerConvention(psPatientId: string, conventionId: number): Observable<void> {
		const params = new HttpParams().set('conventionId', conventionId);
		return this.ioHttp.post<void>(
			`${this.baseUrl}/entities/patients/${psPatientId}/amc/selectionnerConvention`, null,
			{
				params: params,
				...AuthenticatedRequestOptionBuilder.buildAuthenticatedRequestOptions()
			}
		)
			.pipe(
				tap(_ => this.triggerRefreshCouvertureSubject()),
				catchError(poError => {
					console.error("PAT.S:: Erreur lors de l'appel à selectionner convention :", poError);
					this.isvcUiMessage.showMessage(
						new ShowMessageParamsPopup({ header: `Erreur dans la selection de la convention.`, message: poError.message })
					);
					return of(undefined);
				})
			);
	}

	public static getFilteredGarantieOptions(psAmoId: string): ISelectOption<EGarantie>[] {
		psAmoId = IdHelper.extractIdWithoutPrefix(psAmoId, C_PREFIX_AMO);
		const laOptions: ISelectOption<EGarantie>[] = [{ label: "Aucune", value: undefined }];

		switch (psAmoId.slice(0, 2)) {
			case "01":
				laOptions.push(...[
					{ label: "Non exonéré (60%)", value: EGarantie.NONEXO },
					{ label: "FNS (80%)", value: EGarantie.TXFSV },
					{ label: "Régime local (Alsace-Moselle) (90%)", value: EGarantie.TXALMO },
					{ label: "Assuré ou bénéficiaire exonéré (100%)", value: EGarantie['100TOUT'] },

				]);
				break;

			case "02":
				laOptions.push(...[
					{ label: "Non exonérés (accidents non couverts) (60%)", value: EGarantie.NONEXOACC },
					{ label: "Autres cas non exonérés (accidents non couverts) (60%)", value: EGarantie.AUTREACC },
					{ label: "Régime local (Alsace-Moselle) (90%)", value: EGarantie.TXALMO },
				]);
				break;

			case "03":
				laOptions.push(...[
					{ label: "Non exonéré (60%)", value: EGarantie.NONEXO },
					{ label: "Assuré ou bénéficiaire exonéré (100%)", value: EGarantie["100TOUT"] },
				]);
				break;

			case "04":
				laOptions.push(...[
					{ label: "Non exonéré (75%)", value: EGarantie["75TOUT"] },
					{ label: "FNS (80%)", value: EGarantie.TXFSV },
					{ label: "Régime local (Alsace-Moselle) (90%)", value: EGarantie.TXALMO },
					{ label: "SNCF (100%)", value: EGarantie["100SNCF"] },
					{ label: "Assuré ou bénéficiaire exonéré (100%)", value: EGarantie["100TOUT"] },
				]);
				break;

			case "05":
				laOptions.push(...[
					{ label: "Non exonéré (60%)", value: EGarantie.NONEXO },
					{ label: "FNS (80%)", value: EGarantie.TXFSV },
					{ label: "Assuré ou bénéficiaire exonéré (100%)", value: EGarantie["100TOUT"] },
				]);
				break;

			case "06":
				laOptions.push(...[
					{ label: "Non exonéré (60%)", value: EGarantie.NONEXO }
				]);
				break;

			case "07":
				laOptions.push(...[
					{ label: "Assuré ou bénéficiaire exonéré (100%)", value: EGarantie["100TOUT"] },
				]);
				break;

			case "08":
				laOptions.push(...[
					{ label: "Non exonéré (60%)", value: EGarantie.NONEXO },
					{ label: "Assuré ou bénéficiaire exonéré (100%)", value: EGarantie["100TOUT"] }
				]);
				break;

			case "09":
				laOptions.push(...[
					{ label: "Non exonéré (60%)", value: EGarantie.NONEXO },
					{ label: "Assuré ou bénéficiaire exonéré (100%)", value: EGarantie["100TOUT"] },
				]);
				break;
		}

		switch (psAmoId.slice(0, 1)) {
			case "1":
				laOptions.push(...[
					{ label: "Non exonéré (60%)", value: EGarantie.NONEXO },
					{ label: "Assuré ou bénéficiaire exonéré (100%)", value: EGarantie["100TOUT"] },
				]);
				break;

			case "9":
				laOptions.push(...[
					{ label: "Non exonéré (60%)", value: EGarantie.NONEXO },
					{ label: "FNS (80%)", value: EGarantie.TXFSV },
					{ label: "Assuré ou bénéficiaire exonéré (100%)", value: EGarantie["100TOUT"] },

				]);
				break;
		}

		return laOptions;
	}

	public getLatestActiveCouverture(couvertures: (AMOP | AMCP)[]): AMOP | AMCP | null {
		const activeCouvertures = couvertures
			.filter((couverture) => couverture.isActiveAnakin)
			.sort((a, b) =>
				(new Date(b.dateDebut).getTime() || 0) - (new Date(a.dateDebut).getTime() || 0)
			);

		if (activeCouvertures.length > 0) {
			return activeCouvertures[0];
		}

		const expiredCouvertures = couvertures
			.filter((couverture) =>
				couverture.dateFin && new Date(couverture.dateFin).getTime() < Date.now()
			)
			.sort((a, b) =>
				(new Date(b.dateFin).getTime() || 0) - (new Date(a.dateFin).getTime() || 0)
			);

		return expiredCouvertures.length > 0 ? expiredCouvertures[0] : null;
	}

	public areAllDatesEligible(couverture: ICouvertureP, dates: Date[]): boolean {
		if (!couverture.dateDebut && !couverture.dateFin) return true;
		const today = new Date();

		// Si aucune date n’est fournie, on teste la date du jour
		const datesToCheck = dates && dates.length > 0 ? dates : [today];

		return datesToCheck.every(date => {
			const seanceDate = new Date(date);

			if (couverture.dateDebut && couverture.dateFin) {
				return DateHelper.isBetweenTwoDates(
					seanceDate,
					DateHelper.resetDay(couverture.dateDebut),
					DateHelper.fillDay(couverture.dateFin)
				);
			}

			if (couverture.dateDebut) {
				return DateHelper.isBetweenTwoDates(
					seanceDate,
					DateHelper.resetDay(couverture.dateDebut),
					DateHelper.fillDay(new Date(8640000000000000))
				);
			}

			if (couverture.dateFin) {
				return DateHelper.isBetweenTwoDates(
					seanceDate,
					DateHelper.resetDay(new Date(-8640000000000000)),
					DateHelper.fillDay(couverture.dateFin)
				);
			}

			return true;
		});
	}

	public isEligibleFacturationFromSeances(couverture: ICouvertureP, seances: StoredSeance[]): boolean {
		const dates = seances?.map(s => new Date(s.startDate)) || [];
		return this.areAllDatesEligible(couverture, dates);
	}

	public isEligibleFacturationFromPrestations(couverture: ICouvertureP, prestations: IdlPrestation[]): boolean {
		const dates = prestations?.map(p => new Date(p.dateFromId)) || [];
		return this.areAllDatesEligible(couverture, dates);
	}
	//#endregion

}
